import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, AlertCircle, CheckCircle2, GraduationCap, Plus, Trash2, Upload, X, User } from "lucide-react";
import { toast } from "sonner";
import { compressImage } from "@/lib/imageCompress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicEnrollPage() {
  const { branchId, schoolSlug, branchSlug } = useParams();
  const usingSlug = !!(schoolSlug && branchSlug);
  const infoUrl = usingSlug
    ? `${API}/public/enroll/by-slug/${schoolSlug}/${branchSlug}`
    : `${API}/public/enroll/${branchId}`;
  const submitUrl = infoUrl; // POST hits the same path as GET
  const [branchInfo, setBranchInfo] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    name: "", middle_initial: "", last_name: "",
    birthday: "", address: "", city: "", province: "", country: "Philippines",
    notes_from_parent: "",
    photo_base64: "",
  });
  const [guardians, setGuardians] = useState([
    { name: "", phone: "", email: "", fb_messenger: "" },
  ]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(infoUrl);
        if (mounted) setBranchInfo(data);
      } catch (e) {
        if (mounted) setLoadErr(e?.response?.data?.detail || "Enrollment link is not valid");
      }
    })();
    return () => { mounted = false; };
  }, [infoUrl]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setGuardian = (i, k, v) => setGuardians((arr) => arr.map((g, idx) => idx === i ? { ...g, [k]: v } : g));
  const addGuardian = () => setGuardians((arr) => [...arr, { name: "", phone: "", email: "", fb_messenger: "" }]);
  const removeGuardian = (i) => setGuardians((arr) => arr.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Please enter the student's first name"); return; }
    setSubmitting(true);
    try {
      await axios.post(submitUrl, {
        ...form,
        // Drop totally-empty guardian rows so the staff queue isn't noisy.
        guardians: guardians.filter((g) => (g.name || g.phone || g.email || g.fb_messenger || "").trim()),
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (loadErr) {
    return (
      <Page>
        <Card>
          <div className="text-center py-10">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FDECEA] flex items-center justify-center mb-3"><AlertCircle className="w-7 h-7 text-[#C62828]" /></div>
            <h1 className="font-heading text-xl font-semibold text-foreground">Enrollment link not valid</h1>
            <p className="text-sm text-brand-inkMuted mt-1">{loadErr}</p>
          </div>
        </Card>
      </Page>
    );
  }

  if (!branchInfo) {
    return (
      <Page>
        <Card>
          <div className="flex items-center justify-center py-12 text-brand-inkMuted">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading…
          </div>
        </Card>
      </Page>
    );
  }

  if (done) {
    return (
      <Page>
        <Card>
          <div className="text-center py-10" data-testid="enroll-success">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#E8F5E9] flex items-center justify-center mb-3"><CheckCircle2 className="w-7 h-7 text-[#2E7D32]" /></div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Thank you!</h1>
            <p className="text-sm text-brand-inkMuted mt-1.5 max-w-sm mx-auto">Your enrollment has been submitted to <strong className="text-foreground">{branchInfo.branch_name}</strong>. The school will contact you shortly to confirm.</p>
          </div>
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      <Card>
        <Header school={branchInfo.school_name} branch={branchInfo.branch_name} logo={branchInfo.school_logo_url} />
        <form onSubmit={submit} className="space-y-6 pt-6" data-testid="enroll-form">
          <Section title="Student Information">
            <StudentPhotoUploader
              value={form.photo_base64}
              onChange={(v) => setField("photo_base64", v)}
            />
            <Grid>
              <Fld label="First Name" required>
                <Input value={form.name} onChange={(e) => setField("name", e.target.value)} required className="rounded-2xl h-12" data-testid="enroll-name" />
              </Fld>
              <Fld label="Middle Initial">
                <Input value={form.middle_initial} onChange={(e) => setField("middle_initial", e.target.value)} maxLength={3} className="rounded-2xl h-12" data-testid="enroll-mi" />
              </Fld>
              <Fld label="Last Name" colSpan={2}>
                <Input value={form.last_name} onChange={(e) => setField("last_name", e.target.value)} className="rounded-2xl h-12" data-testid="enroll-last-name" />
              </Fld>
              <Fld label="Birthday">
                <Input type="date" value={form.birthday} onChange={(e) => setField("birthday", e.target.value)} className="rounded-2xl h-12" data-testid="enroll-birthday" />
              </Fld>
              <Fld label="City / Town">
                <Input value={form.city} onChange={(e) => setField("city", e.target.value)} className="rounded-2xl h-12" data-testid="enroll-city" />
              </Fld>
              <Fld label="Address" colSpan={2}>
                <Input value={form.address} onChange={(e) => setField("address", e.target.value)} className="rounded-2xl h-12" data-testid="enroll-address" />
              </Fld>
              <Fld label="Province">
                <Input value={form.province} onChange={(e) => setField("province", e.target.value)} className="rounded-2xl h-12" data-testid="enroll-province" />
              </Fld>
              <Fld label="Country">
                <Input value={form.country} onChange={(e) => setField("country", e.target.value)} className="rounded-2xl h-12" data-testid="enroll-country" />
              </Fld>
            </Grid>
          </Section>

          <Section title="Guardian Information">
            <div className="space-y-4">
              {guardians.map((g, i) => (
                <div key={i} className="rounded-2xl border border-brand-purple/15 bg-[#F6F8FE]/40 p-4 space-y-3" data-testid={`enroll-guardian-${i}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-heading text-sm font-semibold text-foreground">Guardian {i + 1}</p>
                    {guardians.length > 1 && (
                      <button type="button" onClick={() => removeGuardian(i)} className="text-brand-inkMuted hover:text-[#C62828]" aria-label="Remove guardian" data-testid={`enroll-guardian-remove-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <Grid>
                    <Fld label="Full Name" colSpan={2}>
                      <Input value={g.name} onChange={(e) => setGuardian(i, "name", e.target.value)} className="rounded-2xl h-12 bg-white" data-testid={`enroll-guardian-name-${i}`} />
                    </Fld>
                    <Fld label="Phone Number">
                      <Input value={g.phone} onChange={(e) => setGuardian(i, "phone", e.target.value)} type="tel" inputMode="tel" className="rounded-2xl h-12 bg-white" data-testid={`enroll-guardian-phone-${i}`} />
                    </Fld>
                    <Fld label="Email">
                      <Input type="email" value={g.email} onChange={(e) => setGuardian(i, "email", e.target.value)} inputMode="email" className="rounded-2xl h-12 bg-white" data-testid={`enroll-guardian-email-${i}`} />
                    </Fld>
                    <Fld label="Messenger" colSpan={2}>
                      <Input value={g.fb_messenger} onChange={(e) => setGuardian(i, "fb_messenger", e.target.value)} className="rounded-2xl h-12 bg-white" data-testid={`enroll-guardian-fb-${i}`} placeholder="m.me/username" />
                    </Fld>
                  </Grid>
                </div>
              ))}
              <Button type="button" onClick={addGuardian} variant="outline" className="rounded-2xl h-11 w-full sm:w-auto" data-testid="enroll-guardian-add">
                <Plus className="w-4 h-4 mr-1.5" /> Add another guardian
              </Button>
            </div>
          </Section>

          <Section title="Notes for the school (optional)">
            <Textarea value={form.notes_from_parent} onChange={(e) => setField("notes_from_parent", e.target.value)} placeholder="Any preferences, allergies, schedule constraints, etc." className="rounded-2xl min-h-[100px]" data-testid="enroll-notes" />
          </Section>

          <Button type="submit" disabled={submitting} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-12 w-full text-base" data-testid="enroll-submit">
            {submitting ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Submitting…</>) : "Submit Enrollment"}
          </Button>
          <p className="text-[11px] text-brand-inkMuted text-center">By submitting, you agree the school may contact you using the details above.</p>
        </form>
      </Card>
    </Page>
  );
}

function Page({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F6F8FE] via-white to-[#F6F8FE] py-6 px-4 sm:py-10">
      <div className="max-w-xl mx-auto">{children}</div>
    </div>
  );
}
function Card({ children }) {
  return <div className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-5 sm:p-7">{children}</div>;
}
function Header({ school, branch, logo }) {
  return (
    <div className="border-b border-brand-purple/10 pb-5 text-center">
      {logo ? (
        <img src={logo} alt={school} className="w-14 h-14 mx-auto rounded-2xl mb-3 object-cover border border-brand-purple/10" />
      ) : (
        <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-purple/10 flex items-center justify-center mb-3">
          <GraduationCap className="w-7 h-7 text-brand-purple" />
        </div>
      )}
      <p className="text-[11px] uppercase tracking-[0.18em] text-brand-inkMuted font-heading">Student Enrollment</p>
      <h1 className="font-heading text-2xl font-bold text-foreground mt-1">{school || "—"}</h1>
      <p className="text-sm text-brand-inkMuted mt-0.5">Branch: <span className="font-medium text-foreground">{branch || "—"}</span></p>
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-brand-inkMuted font-heading mb-3">{title}</p>
      {children}
    </div>
  );
}
function Grid({ children }) { return <div className="grid sm:grid-cols-2 gap-3">{children}</div>; }
function Fld({ label, required, colSpan, children }) {
  return (
    <div className={colSpan === 2 ? "sm:col-span-2" : ""}>
      <Label className="font-heading text-xs uppercase tracking-wider text-brand-inkMuted">
        {label}{required ? <span className="text-[#C62828] ml-1">*</span> : null}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}


/**
 * Public-facing student photo uploader. Compressed to keep the enrollment
 * POST small (parents may be on mobile data). Empty state shows a circular
 * placeholder + a friendly "Upload photo" affordance.
 */
function StudentPhotoUploader({ value, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file (JPG, PNG).");
      return;
    }
    setBusy(true);
    try {
      const compressed = await compressImage(file, { maxKB: 400, maxDim: 800 });
      onChange(compressed);
    } catch (err) {
      toast.error("Could not read that image. Try a different photo.");
    } finally { setBusy(false); }
  };

  return (
    <div className="mb-5 flex items-center gap-4" data-testid="enroll-photo-uploader">
      {value ? (
        <img
          src={value}
          alt="Student"
          className="w-24 h-24 rounded-full object-cover border-2 border-brand-purple/20 shadow-sm"
        />
      ) : (
        <div className="w-24 h-24 rounded-full bg-brand-purple/10 flex items-center justify-center border-2 border-dashed border-brand-purple/30">
          <User className="w-10 h-10 text-brand-purple/60" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-heading font-semibold text-foreground">Student Photo (optional)</p>
        <p className="text-xs text-brand-inkMuted mt-0.5 mb-2">JPG or PNG · used on the student profile & ID.</p>
        <div className="flex gap-2 flex-wrap">
          <Button
            type="button" size="sm" variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="rounded-full"
            data-testid="enroll-photo-btn"
          >
            <Upload className="w-3.5 h-3.5 mr-1" />
            {busy ? "Uploading..." : value ? "Replace" : "Upload photo"}
          </Button>
          {value && (
            <Button
              type="button" size="sm" variant="ghost"
              onClick={() => onChange("")}
              className="rounded-full text-[#C62828] hover:bg-[#FFEBEE]"
              data-testid="enroll-photo-remove"
            >
              <X className="w-3.5 h-3.5 mr-1" /> Remove
            </Button>
          )}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="user"
        onChange={handleFile}
        className="hidden"
      />
    </div>
  );
}
